import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import Icon from '../Icon';

export default function ActorModelInfographic() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [crashStep, setCrashStep] = useState(0);

  return (
    <div ref={ref} className="relative w-full">
      {/* Friendly explanation */}
      <div className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="smart_toy" className="text-2xl text-purple-600" /> What's an Actor?
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Imagine each auction has its own <strong>personal assistant</strong>. This assistant:
        </p>
        <ul className="mt-3 space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Has a <strong>mailbox</strong> where bids arrive</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Opens letters <strong>one at a time</strong>, in order</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Never gets confused because they <strong>never multitask</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>Remembers everything in their <strong>personal notebook</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="small" className="text-green-500 mt-1" />
            <span>If they crash, their <strong>supervisor</strong> (Cloudflare) restarts them!</span>
          </li>
        </ul>
      </div>

      {/* WHY ACTORS - The Problem */}
      <div className="mb-6 bg-white rounded-2xl border border-gray-200 p-6">
        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
          <Icon name="help_outline" size="small" /> Why Not Just Use Normal Code?
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Traditional approach - BAD */}
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <h5 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
              <Icon name="cancel" size="small" /> Traditional: Shared State
            </h5>
            <svg viewBox="0 0 200 150" className="w-full h-auto mb-3">
              <rect x="0" y="0" width="200" height="150" fill="#fef2f2" rx="8" />
              
              {/* Database */}
              <rect x="70" y="80" width="60" height="40" rx="4" fill="#fee2e2" stroke="#dc2626" strokeWidth="2" />
              <text x="100" y="105" textAnchor="middle" fontSize="10" fontWeight="500">Database</text>
              
              {/* Multiple requests */}
              <motion.g animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 0.5, repeat: Infinity }}>
                <circle cx="40" cy="40" r="15" fill="#fca5a5" />
                <text x="40" y="45" textAnchor="middle" fontSize="10">Req 1</text>
                <line x1="55" y1="50" x2="75" y2="80" stroke="#dc2626" strokeWidth="2" />
              </motion.g>
              
              <motion.g animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 0.5, repeat: Infinity }}>
                <circle cx="100" cy="30" r="15" fill="#fca5a5" />
                <text x="100" y="35" textAnchor="middle" fontSize="10">Req 2</text>
                <line x1="100" y1="45" x2="100" y2="80" stroke="#dc2626" strokeWidth="2" />
              </motion.g>
              
              <motion.g animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 0.5, repeat: Infinity, delay: 0.25 }}>
                <circle cx="160" cy="40" r="15" fill="#fca5a5" />
                <text x="160" y="45" textAnchor="middle" fontSize="10">Req 3</text>
                <line x1="145" y1="50" x2="125" y2="80" stroke="#dc2626" strokeWidth="2" />
              </motion.g>
              
              <text x="100" y="140" textAnchor="middle" fontSize="9" fill="#991b1b" fontWeight="500">
                Race condition! Who wins?
              </text>
            </svg>
            <p className="text-sm text-red-700">
              Multiple requests hit the database <strong>at the same time</strong>. 
              You need locks, transactions, retry logic...
            </p>
          </div>

          {/* Actor approach - GOOD */}
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <h5 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
              <Icon name="check_circle" size="small" /> Actor Model: Isolated State
            </h5>
            <svg viewBox="0 0 200 150" className="w-full h-auto mb-3">
              <rect x="0" y="0" width="200" height="150" fill="#f0fdf4" rx="8" />
              
              {/* Actor with queue */}
              <rect x="100" y="60" width="80" height="50" rx="8" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
              <text x="140" y="85" textAnchor="middle" fontSize="10" fontWeight="600" fill="#166534">Actor</text>
              <text x="140" y="100" textAnchor="middle" fontSize="8" fill="#166534">Actor</text>
              
              {/* Queue */}
              <rect x="20" y="70" width="70" height="30" rx="4" fill="#bbf7d0" stroke="#86efac" strokeWidth="1" />
              <text x="55" y="90" textAnchor="middle" fontSize="9" fill="#166534">Queue</text>
              
              {/* Requests in queue */}
              <motion.g
                initial={{ x: -20 }}
                animate={{ x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <rect x="25" y="75" width="18" height="18" rx="2" fill="#22c55e" />
                <text x="34" y="87" textAnchor="middle" fontSize="7" fill="white">1</text>
              </motion.g>
              <rect x="45" y="75" width="18" height="18" rx="2" fill="#86efac" />
              <text x="54" y="87" textAnchor="middle" fontSize="7" fill="#166534">2</text>
              <rect x="65" y="75" width="18" height="18" rx="2" fill="#86efac" />
              <text x="74" y="87" textAnchor="middle" fontSize="7" fill="#166534">3</text>
              
              {/* Arrow */}
              <motion.path
                d="M 92 85 L 98 85"
                stroke="#22c55e"
                strokeWidth="2"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              
              <text x="100" y="140" textAnchor="middle" fontSize="9" fill="#166534" fontWeight="500">
                One at a time. No race conditions!
              </text>
            </svg>
            <p className="text-sm text-green-700">
              Requests queue up and get processed <strong>one by one</strong>. 
              Simple, safe, predictable!
            </p>
          </div>
        </div>
      </div>

      {/* Animated Infographic */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <svg viewBox="0 0 500 280" className="w-full h-auto">
          {/* Background */}
          <rect x="0" y="0" width="500" height="280" fill="#fafafa" rx="12" />
          
          {/* Title */}
          <text x="250" y="30" textAnchor="middle" fontSize="14" fill="#374151" fontWeight="600">
            The Actor Model: One Assistant Per Auction
          </text>

          {/* Bidders sending messages */}
          <motion.g
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {/* Person 1 */}
            <circle cx="50" cy="100" r="20" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2" />
            <text x="50" y="105" textAnchor="middle" fontSize="10" fontWeight="500" fill="#6366f1">User</text>
            <text x="50" y="135" textAnchor="middle" fontSize="10" fill="#6366f1">Alice</text>
          </motion.g>

          <motion.g
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Person 2 */}
            <circle cx="50" cy="180" r="20" fill="#fce7f3" stroke="#ec4899" strokeWidth="2" />
            <text x="50" y="185" textAnchor="middle" fontSize="10" fontWeight="500" fill="#ec4899">User</text>
            <text x="50" y="215" textAnchor="middle" fontSize="10" fill="#ec4899">Bob</text>
          </motion.g>

          {/* Letters flying to mailbox */}
          <motion.g
            initial={{ x: 50, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5, type: 'spring' }}
          >
            <rect x="100" y="85" width="40" height="30" rx="3" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" />
            <text x="120" y="105" textAnchor="middle" fontSize="10" fontWeight="500" fill="#92400e">Bid</text>
          </motion.g>

          <motion.g
            initial={{ x: 50, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.7, type: 'spring' }}
          >
            <rect x="100" y="165" width="40" height="30" rx="3" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" />
            <text x="120" y="185" textAnchor="middle" fontSize="10" fontWeight="500" fill="#92400e">Bid</text>
          </motion.g>

          {/* Arrows to mailbox */}
          <motion.path
            d="M 145 100 L 180 140"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeDasharray="4 2"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.4, delay: 0.9 }}
          />
          <motion.path
            d="M 145 180 L 180 160"
            stroke="#9ca3af"
            strokeWidth="2"
            strokeDasharray="4 2"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.4, delay: 1.0 }}
          />

          {/* Mailbox */}
          <motion.g
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.3, type: 'spring' }}
          >
            <rect x="185" y="110" width="80" height="80" rx="8" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
            <text x="225" y="140" textAnchor="middle" fontSize="11" fontWeight="600" fill="#1d4ed8">INBOX</text>
            <text x="225" y="160" textAnchor="middle" fontSize="10" fill="#1d4ed8" fontWeight="500">Mailbox</text>
            <text x="225" y="175" textAnchor="middle" fontSize="8" fill="#6b7280">(bids queue up)</text>
          </motion.g>

          {/* Arrow from mailbox to assistant */}
          <motion.path
            d="M 270 150 L 310 150"
            stroke="#3b82f6"
            strokeWidth="3"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.4, delay: 1.3 }}
          />
          <motion.polygon
            points="315,150 305,145 305,155"
            fill="#3b82f6"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 1.6 }}
          />
          <motion.text
            x="290"
            y="140"
            textAnchor="middle"
            fontSize="8"
            fill="#6b7280"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 1.5 }}
          >
            one at a time
          </motion.text>

          {/* Personal Assistant (Actor) */}
          <motion.g
            initial={{ scale: 0 }}
            animate={isInView ? { scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.5, type: 'spring' }}
          >
            <rect x="320" y="90" width="120" height="120" rx="12" fill="#f0fdf4" stroke="#22c55e" strokeWidth="2" />
            <text x="380" y="120" textAnchor="middle" fontSize="12" fontWeight="700" fill="#166534">ACTOR</text>
            <text x="380" y="145" textAnchor="middle" fontSize="11" fill="#166534" fontWeight="600">Auction Assistant</text>
            <text x="380" y="160" textAnchor="middle" fontSize="8" fill="#6b7280">(The "Actor")</text>
            
            {/* Notebook */}
            <rect x="350" y="170" width="60" height="25" rx="4" fill="#dcfce7" stroke="#86efac" strokeWidth="1" />
            <text x="380" y="187" textAnchor="middle" fontSize="8" fill="#166534">State</text>
          </motion.g>

          {/* Processing indicator */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: [0, 1, 1, 0] } : {}}
            transition={{ duration: 2, delay: 2, repeat: Infinity }}
          >
            <rect x="335" y="135" width="90" height="20" rx="4" fill="#bbf7d0" />
            <text x="380" y="149" textAnchor="middle" fontSize="8" fill="#166534" fontWeight="500">
              Processing bid...
            </text>
          </motion.g>

          {/* Key point callout */}
          <motion.g
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 2 }}
          >
            <rect x="130" y="230" width="240" height="35" rx="6" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1" />
            <text x="250" y="250" textAnchor="middle" fontSize="10" fill="#92400e" fontWeight="500">
              One message at a time = No confusion, ever!
            </text>
          </motion.g>
        </svg>
      </div>

      {/* LET IT CRASH Philosophy */}
      <div className="mb-6 bg-white rounded-2xl border border-gray-200 p-6">
        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
          <Icon name="warning" size="small" className="text-orange-500" /> The "Let It Crash" Philosophy
        </h4>
        
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-5 mb-4 border border-orange-200">
          <p className="text-gray-700 mb-3">
            Traditional code tries to handle <strong>every possible error</strong>. 
            Actor systems have a different philosophy:
          </p>
          <blockquote className="border-l-4 border-orange-400 pl-4 italic text-gray-600 mb-3">
            "If an actor crashes, <strong>let it crash</strong>. 
            Its supervisor will decide what happens next."
          </blockquote>
          <p className="text-sm text-gray-600">
            This sounds scary, but it's actually <strong>safer</strong>! Instead of corrupted half-states 
            from failed error recovery, we get a <strong>clean restart</strong>.
          </p>
        </div>

        {/* Crash & Restart Animation */}
        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Watch what happens when an Actor crashes:</span>
            <button
              onClick={() => setCrashStep(s => (s + 1) % 4)}
              className="text-sm bg-orange-100 text-orange-700 px-3 py-1 rounded-lg hover:bg-orange-200 transition-colors flex items-center gap-1"
            >
              <Icon name={crashStep === 0 ? 'play_arrow' : crashStep < 3 ? 'skip_next' : 'replay'} size="small" />
              {crashStep === 0 ? 'Start' : crashStep < 3 ? 'Next' : 'Replay'}
            </button>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-4">
            {/* Step 1: Running */}
            <motion.div
              animate={{
                opacity: crashStep >= 0 ? 1 : 0.3,
                scale: crashStep === 0 ? 1.05 : 1
              }}
              className={`flex-1 p-4 rounded-xl text-center ${
                crashStep === 0 ? 'bg-green-100 border-2 border-green-400' : 'bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon name="smart_toy" className="text-3xl text-green-600 mb-2" />
              <div className="font-semibold text-gray-900">Running</div>
              <div className="text-xs text-gray-500">Happy actor</div>
            </motion.div>

            <Icon name="arrow_forward" className="text-gray-400" />

            {/* Step 2: Crash */}
            <motion.div
              animate={{
                opacity: crashStep >= 1 ? 1 : 0.3,
                scale: crashStep === 1 ? 1.05 : 1,
                rotate: crashStep === 1 ? [0, -5, 5, 0] : 0
              }}
              transition={{ rotate: { duration: 0.3 } }}
              className={`flex-1 p-4 rounded-xl text-center ${
                crashStep === 1 ? 'bg-red-100 border-2 border-red-400' : 'bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon name="error" className="text-3xl text-red-600 mb-2" />
              <div className="font-semibold text-gray-900">Crash!</div>
              <div className="text-xs text-gray-500">Error happens</div>
            </motion.div>

            <Icon name="arrow_forward" className="text-gray-400" />

            {/* Step 3: Supervisor */}
            <motion.div
              animate={{
                opacity: crashStep >= 2 ? 1 : 0.3,
                scale: crashStep === 2 ? 1.05 : 1
              }}
              className={`flex-1 p-4 rounded-xl text-center ${
                crashStep === 2 ? 'bg-purple-100 border-2 border-purple-400' : 'bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon name="support_agent" className="text-3xl text-purple-600 mb-2" />
              <div className="font-semibold text-gray-900">Supervisor</div>
              <div className="text-xs text-gray-500">Cloudflare steps in</div>
            </motion.div>

            <Icon name="arrow_forward" className="text-gray-400" />

            {/* Step 4: Restart */}
            <motion.div
              animate={{
                opacity: crashStep >= 3 ? 1 : 0.3,
                scale: crashStep === 3 ? 1.05 : 1
              }}
              className={`flex-1 p-4 rounded-xl text-center ${
                crashStep === 3 ? 'bg-green-100 border-2 border-green-400' : 'bg-gray-100 border border-gray-200'
              }`}
            >
              <Icon name="check_circle" className="text-3xl text-green-600 mb-2" />
              <div className="font-semibold text-gray-900">Restarted!</div>
              <div className="text-xs text-gray-500">State recovered</div>
            </motion.div>
          </div>
        </div>

        {/* Cloudflare as Supervisor */}
        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
          <div className="flex items-start gap-3">
            <Icon name="cloud" className="text-2xl text-purple-600" />
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Cloudflare is Our Supervisor!</h5>
              <p className="text-sm text-gray-600 mb-2">
                In traditional actor systems (like Akka), you define supervisor strategies. 
                With Durable Objects, <strong>Cloudflare handles this automatically</strong>:
              </p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span>Actor crashes? <strong>Automatic restart</strong> on next request</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span>State lost? <strong>Replayed from event log</strong> (Event Sourcing!)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span>Snapshots every 50 events for <strong>fast recovery</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span>Network issues? <strong>Cloudflare retries</strong> transparently</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Real-world analogy */}
      <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
        <div className="flex items-start gap-3">
          <Icon name="storefront" className="text-2xl text-blue-600" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Think of a Post Office Counter</h4>
            <p className="text-sm text-gray-600">
              Each clerk serves <strong>one customer at a time</strong>. Even if 10 people arrive at once, 
              they queue up and get served in order. No one gets skipped, no one gets mixed up. 
              That's exactly how an Actor works with bids!
            </p>
          </div>
        </div>
      </div>

      {/* Let It Crash Analogy */}
      <div className="mt-4 bg-orange-50 rounded-xl p-5 border border-orange-100">
        <div className="flex items-start gap-3">
          <Icon name="local_hospital" className="text-2xl text-orange-600" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">"Let It Crash" = A Hospital ER</h4>
            <p className="text-sm text-gray-600">
              If a patient has a seizure, doctors don't panic trying to prevent <em>every possible complication</em>. 
              They let it happen safely, then <strong>stabilize and recover</strong>. 
              Our actors work the same way: crash safely, restart cleanly, no corrupt state.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
